export interface PolarKnot {
  angleDeg: number;
  r: number;
}

export interface PchipPolarSample {
  angleDeg: number;
  r: number;
}

const DEFAULT_MIN_STEPS_PER_SEGMENT = 8;
const DEGREES_PER_STEP = 4;

/** Cartesian position from polar chart angle (degrees) and scaled radius */
export function polarKnotToXY(angleDeg: number, r: number): { x: number; y: number } {
  const radians = angleDeg * Math.PI / 180;
  return {
    x: r * Math.cos(radians),
    y: r * Math.sin(radians),
  };
}

/** Sort knots by angle and drop duplicate angles (keep last). */
export function normalizePolarKnots(knots: PolarKnot[]): PolarKnot[] {
  const sorted = [...knots].sort((a, b) => a.angleDeg - b.angleDeg);
  const deduped: PolarKnot[] = [];

  for (const knot of sorted) {
    const last = deduped[deduped.length - 1];
    if (last && last.angleDeg === knot.angleDeg) {
      deduped[deduped.length - 1] = knot;
    } else {
      deduped.push(knot);
    }
  }

  return deduped;
}

/**
 * Fritsch–Carlson PCHIP tangents dy/dx at each knot.
 * Shape-preserving: no overshoot on monotone data; zero slope at sign changes.
 */
export function computePchipSlopes(x: number[], y: number[]): number[] {
  const n = x.length;
  if (n === 0) return [];
  if (n === 1) return [0];
  if (n === 2) {
    const slope = (y[1] - y[0]) / (x[1] - x[0]);
    return [slope, slope];
  }

  const h: number[] = [];
  const delta: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const hi = x[i + 1] - x[i];
    h.push(hi);
    delta.push((y[i + 1] - y[i]) / hi);
  }

  const m = new Array<number>(n).fill(0);

  for (let k = 1; k < n - 1; k++) {
    if (delta[k - 1] === 0 || delta[k] === 0 || delta[k - 1] * delta[k] <= 0) {
      m[k] = 0;
      continue;
    }

    const w1 = 2 * h[k] + h[k - 1];
    const w2 = h[k] + 2 * h[k - 1];
    m[k] = (w1 + w2) / (w1 / delta[k - 1] + w2 / delta[k]);
  }

  m[0] = endpointPchipSlope(h[0], h[1], delta[0], delta[1]);
  m[n - 1] = endpointPchipSlope(h[n - 2], h[n - 3], delta[n - 2], delta[n - 3]);

  return m;
}

function endpointPchipSlope(h0: number, h1: number, d0: number, d1: number): number {
  let slope = ((2 * h0 + h1) * d0 - h0 * d1) / (h0 + h1);

  if (d0 * d1 <= 0) {
    slope = 0;
  } else if (Math.sign(slope) !== Math.sign(d0)) {
    slope = 0;
  } else if (Math.abs(slope) > 3 * Math.abs(d0)) {
    slope = 3 * d0;
  }

  return slope;
}

/** Evaluate PCHIP interpolant r(theta) at a single angle. */
export function evaluatePchipAt(
  knots: PolarKnot[],
  thetaDeg: number,
  slopes?: number[],
): number | null {
  const normalized = normalizePolarKnots(knots);
  if (normalized.length === 0) return null;
  if (normalized.length === 1) return normalized[0].r;

  const x = normalized.map(k => k.angleDeg);
  const y = normalized.map(k => k.r);
  const m = slopes ?? computePchipSlopes(x, y);

  if (thetaDeg < x[0] || thetaDeg > x[x.length - 1]) {
    return null;
  }

  for (let i = 0; i < x.length - 1; i++) {
    if (thetaDeg < x[i] || thetaDeg > x[i + 1]) continue;
    if (thetaDeg === x[i]) return y[i];
    if (thetaDeg === x[i + 1]) return y[i + 1];

    const h = x[i + 1] - x[i];
    const t = (thetaDeg - x[i]) / h;
    const t2 = t * t;
    const t3 = t2 * t;
    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;

    return h00 * y[i] + h10 * h * m[i] + h01 * y[i + 1] + h11 * h * m[i + 1];
  }

  return y[y.length - 1];
}

/** Numerical derivative dr/dtheta at a knot (for tests). */
export function evaluatePchipDerivativeAt(
  knots: PolarKnot[],
  thetaDeg: number,
  epsilon = 0.01,
): number | null {
  const below = evaluatePchipAt(knots, thetaDeg - epsilon);
  const above = evaluatePchipAt(knots, thetaDeg + epsilon);
  if (below == null || above == null) return null;
  return (above - below) / (2 * epsilon);
}

function stepsForSegment(deltaDeg: number, minSteps: number): number {
  return Math.max(minSteps, Math.ceil(Math.abs(deltaDeg) / DEGREES_PER_STEP));
}

/** Dense PCHIP samples along a polar wing. */
export function samplePchipPolar(
  knots: PolarKnot[],
  options?: { minStepsPerSegment?: number },
): PchipPolarSample[] {
  const normalized = normalizePolarKnots(knots);
  if (normalized.length === 0) return [];
  if (normalized.length === 1) return [{ angleDeg: normalized[0].angleDeg, r: normalized[0].r }];

  const minSteps = options?.minStepsPerSegment ?? DEFAULT_MIN_STEPS_PER_SEGMENT;
  const x = normalized.map(k => k.angleDeg);
  const y = normalized.map(k => k.r);
  const m = computePchipSlopes(x, y);
  const samples: PchipPolarSample[] = [];

  for (let i = 0; i < normalized.length - 1; i++) {
    const theta0 = x[i];
    const theta1 = x[i + 1];
    const steps = stepsForSegment(theta1 - theta0, minSteps);

    for (let step = 0; step <= steps; step++) {
      if (step === 0 && samples.length > 0) continue;

      const thetaDeg = step === steps
        ? theta1
        : theta0 + ((theta1 - theta0) * step) / steps;
      const r = evaluatePchipAt(normalized, thetaDeg, m);
      if (r == null) continue;
      samples.push({ angleDeg: thetaDeg, r });
    }
  }

  return samples;
}

/** Build an SVG path for a polar wing using PCHIP interpolation in (theta, r) space. */
export function buildPchipPolarPath(
  knots: PolarKnot[],
  options?: { minStepsPerSegment?: number },
): string {
  const samples = samplePchipPolar(knots, options);
  if (samples.length === 0) return "";

  let path = "";
  samples.forEach((sample, index) => {
    const { x, y } = polarKnotToXY(sample.angleDeg, sample.r);
    path += index === 0 ? `M${x},${y}` : `L${x},${y}`;
  });

  return path;
}
